import { getActivityTypeText } from 'fitbeat.gym-core';
import {
  ActivityType,
  Gym,
  ILeaderboardConfig,
  ILeagueConfig,
  isInterestGroup,
  ITeamConfig,
} from 'fitbeat.models';
import React, { Component, CSSProperties } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { History } from 'history';
import { IAppStore } from '../../rootReducer';
import getLogger from '../../util/logging';
import {
  createLeaderboardConfig,
  getLeaderboardConfigs,
  updateLeaderboardConfig,
} from '../leaderboardsSlice';
import {
  applyInterestGroupInvariants,
  captureLeaderboardFieldsOverriddenByInterestGroup,
  clearInterestGroupFields,
  LeaderboardFieldsOverriddenByInterestGroup,
  isExperiencePointsMetric,
  isTeamParticipationBonusEligible,
  leagueConfigsWhenEnablingLeagues,
  setLeaderboardConfig,
  transformLeaderboardConfig,
} from './helpers';
import InterestGroupSettingsTab from './tabs/InterestGroupSettingsTab';
import GroupCatalogue from '../GroupCatalogue';
import LeaderboardSettingsTab from './tabs/LeaderboardSettingsTab';
import LeaguesTab from './tabs/LeaguesTab';
import TeamSettingsTab from './tabs/TeamSettingsTab';
import TeamsTab from './tabs/TeamsTab';

type ConfigType = 'leaderboard' | 'interest-group' | 'catalogue';

interface IProps {
  selectedGymName: string;
  isFetching: boolean;
  gyms: Gym[];
  isOrgAdmin: boolean;
  formType: 'update' | 'create';
  selectedLeaderboardConfig: ILeaderboardConfig | null;
  history: History;
  createLeaderboardConfig: (
    selectedGymName: string,
    leaderboardConfig: ILeaderboardConfig,
  ) => Promise<void>;
  updateLeaderboardConfig: (
    selectedGymName: string,
    leaderboardConfig: ILeaderboardConfig,
  ) => Promise<void>;
  refreshGymLeaderboardConfigs: (gymName: string) => void;
}

interface IState {
  formData: ILeaderboardConfig;
  allGyms: boolean;
  teamConfigs: ITeamConfig[];
  newTeamName: string;
  allGymsInOrg: string;
  hasUpdated: boolean;
  isSaving: boolean;
  prizesAndNotes: string[];
  newPrizeOrNote: string;
  groupLinkError: string;
  saveError: string;
  selectedActivities: ActivityType[];
  activeTab: 'settings' | 'teams';
  teamManagementTab: 'teamSettings' | 'teams' | 'leagues';
  leagueConfigs: ILeagueConfig[];
  enableLeagues: boolean;
  enableDailyTeamParticipationBonus: boolean;
  configType: ConfigType;
  leaderboardFieldsBeforeInterestGroup?: LeaderboardFieldsOverriddenByInterestGroup;
}

const initialFormData: ILeaderboardConfig = {
  _id: '',
  name: '',
  order: 1,
  startDate: new Date(),
  endDate: undefined,
  frequency: 'one-time',
  organisation: '',
  gymName: '',
  competitionType: 'individual',
  metric: 'total-minutes',
  teams: [],
  teamConfigs: [],
  timezone: 'Australia/Sydney',
  showInStudio: false,
  requireOptIn: false,
  includeImportedWorkouts: true,
  capMinutes: 60,
  isEnabled: true,
  prizesAndNotes: [],
  groupLink: undefined,
  restrictedToActivityTypes: undefined,
};

class LeaderboardConfigForm extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      formData: initialFormData,
      allGyms: false,
      teamConfigs: [],
      newTeamName: '',
      allGymsInOrg: 'All Gyms in Organisation',
      hasUpdated: false,
      isSaving: false,
      prizesAndNotes: [],
      newPrizeOrNote: '',
      groupLinkError: '',
      saveError: '',
      selectedActivities: [],
      activeTab: 'settings',
      teamManagementTab: 'teamSettings',
      leagueConfigs: [],
      enableLeagues: false,
      enableDailyTeamParticipationBonus: false,
      configType: 'leaderboard',
    };

    if (props.formType === 'create') {
      const selectedGym = this.props.gyms.find((gym) => gym.name === this.props.selectedGymName);
      const selectedTimezone = selectedGym ? selectedGym.timezone : 'Australia/Sydney';

      initialFormData.gymName = this.props.isOrgAdmin ? this.state.allGymsInOrg : this.props.selectedGymName;
      initialFormData.timezone = selectedTimezone;
    }

    if (props.formType === 'update' && props.selectedLeaderboardConfig) {
      const { startDate, endDate } = props.selectedLeaderboardConfig;
      const teamConfigs = !props.selectedLeaderboardConfig.teamConfigs
        && props.selectedLeaderboardConfig.teams
        ? props.selectedLeaderboardConfig.teams.map((teamName) => ({ name: teamName }))
        : props.selectedLeaderboardConfig.teamConfigs;

      this.state = {
        ...this.state,
        formData: {
          ...props.selectedLeaderboardConfig,
          startDate: new Date(startDate),
          endDate: endDate ? new Date(endDate) : undefined,
          teamConfigs,
        },
        teamConfigs: [
          ...this.state.teamConfigs,
          ...(!props.selectedLeaderboardConfig.teamConfigs
            ? props.selectedLeaderboardConfig.teams?.map((teamName) => ({ name: teamName })) ?? []
            : []),
          ...props.selectedLeaderboardConfig.teamConfigs ?? [],
        ],
        prizesAndNotes: [
          ...this.state.prizesAndNotes,
          ...props.selectedLeaderboardConfig.prizesAndNotes ?? [],
        ],
        selectedActivities: [
          ...this.state.selectedActivities,
          ...props.selectedLeaderboardConfig.restrictedToActivityTypes ?? [],
        ],
        leagueConfigs: [
          ...this.state.leagueConfigs,
          ...props.selectedLeaderboardConfig.leagueConfig ?? [],
        ],
        enableLeagues: (
          props.selectedLeaderboardConfig.leagueConfig ?? []
        ).length > 0,
        enableDailyTeamParticipationBonus:
          props.selectedLeaderboardConfig.dailyTeamParticipationBonusMaxXp != null,
        configType: isInterestGroup(props.selectedLeaderboardConfig) ? 'interest-group' : 'leaderboard',
      };
    }
  }

  public componentDidMount() {
    if (this.props.formType === 'update' && !this.props.selectedLeaderboardConfig) {
      this.props.history.push('/app/leaderboards');
    }
  }

  public createLeaderboardConfig = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (this.state.configType === 'leaderboard' && this.isBroken()) {
      this.setState({
        saveError: 'This leaderboard is misconfigured: experience points cannot be '
          + 'used with activity type restrictions.',
      });
      return;
    }
    if (this.state.configType === 'leaderboard' && this.isDailyTeamParticipationBonusInvalid()) {
      return;
    }
    transformLeaderboardConfig(
      this.state.formData,
      this.state.allGymsInOrg,
      this.state.teamConfigs,
      this.props.formType === 'update',
      this.state.prizesAndNotes,
      this.state.selectedActivities,
      this.state.leagueConfigs,
      this.state.enableLeagues,
    );
    if (this.state.configType === 'interest-group') {
      applyInterestGroupInvariants(this.state.formData);
    } else {
      clearInterestGroupFields(this.state.formData);
    }
    const logger = getLogger('leaderboard:submit');
    try {
      this.setState({
        isSaving: true,
        saveError: '',
      });
      if (this.props.formType === 'update') {
        await this.props.updateLeaderboardConfig(
          this.props.selectedGymName,
          this.state.formData,
        );
      } else {
        await this.props.createLeaderboardConfig(
          this.props.selectedGymName,
          this.state.formData,
        );
      }

      this.setState({
        formData: initialFormData,
        allGyms: false,
        isSaving: false,
        saveError: '',
        teamConfigs: [],
        activeTab: 'settings',
        teamManagementTab: 'teamSettings',
        leagueConfigs: [],
        configType: 'leaderboard',
        leaderboardFieldsBeforeInterestGroup: undefined,
      });
      this.props.refreshGymLeaderboardConfigs(this.props.selectedGymName);
      this.props.history.push('/app/leaderboards');
    } catch (error) {
      logger.error(error, 'Failed to create leaderboard config');
      this.setState({
        isSaving: false,
        saveError: (error as Error)?.message || 'Failed to save leaderboard config.',
      });
    }
  };

  public discardLeaderboardConfig = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.props.history.push('/app/leaderboards');
  };

  public render() {
    if (this.props.formType === 'update' && !this.props.selectedLeaderboardConfig) {
      return null;
    }

    const { selectedGymName, gyms } = this.props;
    const { orderedGyms, timezones } = setLeaderboardConfig(
      gyms,
      selectedGymName,
      this.props.isOrgAdmin,
      this.state.allGymsInOrg,
    );

    const isTeamsListTab = this.state.activeTab === 'teams'
      && this.state.teamManagementTab === 'teams';
    const stickyActionBarStyle = isTeamsListTab
      ? { ...styles.stickyActionBar, ...styles.stickyActionBarOnTeamsTab }
      : styles.stickyActionBar;
    const isBroken = this.isBroken();
    const isInterestGroupMode = this.state.configType === 'interest-group';
    const isCatalogueMode = this.state.configType === 'catalogue';
    const saveTitle = () => {
      if (this.state.isSaving) {
        return 'Saving';
      }
      return 'Save';
    };

    return (
      <div className='leaderboard-create-container'>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            {isCatalogueMode ? 'Create from catalogue' : (
              <>
                { this.props.formType === 'update' ? 'Update ' : 'Create ' }
                {isInterestGroupMode ? 'Interest Group' : 'Leaderboard'}
              </>
            )}
          </h2>
          <button
            type='button'
            className='jr-btn-no-border pointer'
            style={styles.closeButton}
            onClick={() => this.props.history.goBack()}
          >
            X
          </button>
        </div>
        {this.props.formType === 'create' && (
          <div style={styles.typeRow}>
            <label style={styles.typeOption}>
              <input
                type='radio'
                name='configType'
                value='leaderboard'
                checked={!isInterestGroupMode}
                onChange={() => this.handleConfigTypeChange('leaderboard')}
              />
              Leaderboard
            </label>
            <label style={styles.typeOption}>
              <input
                type='radio'
                name='configType'
                value='interest-group'
                checked={isInterestGroupMode}
                onChange={() => this.handleConfigTypeChange('interest-group')}
              />
              Interest group
            </label>
            <label style={styles.typeOption}>
              <input
                type='radio'
                name='configType'
                value='catalogue'
                checked={isCatalogueMode}
                onChange={() => this.handleConfigTypeChange('catalogue')}
              />
              Create from catalogue
            </label>
          </div>
        )}
        {!isInterestGroupMode && !isCatalogueMode && (
          <div style={styles.tabRow}>
            <button
              type='button'
              className='jr-btn-no-border pointer'
              style={this.state.activeTab === 'settings' ? styles.activeTab : styles.inactiveTab}
              onClick={() => this.setState({ activeTab: 'settings' })}
            >
              Leaderboard Settings
            </button>
            {this.state.formData.competitionType === 'inter-team' && (
              <button
                type='button'
                className='jr-btn-no-border pointer'
                style={this.state.activeTab === 'teams' ? styles.activeTab : styles.inactiveTab}
                onClick={() => this.setState({ activeTab: 'teams' })}
              >
                Team Management
              </button>
            )}
          </div>
        )}
        {!isInterestGroupMode && !isCatalogueMode && this.state.activeTab === 'teams' && (
          <div style={styles.subTabRow}>
            <button
              type='button'
              className='jr-btn-no-border pointer'
              style={
                this.state.teamManagementTab === 'teamSettings'
                  ? { ...styles.activeTab, flex: 1 }
                  : { ...styles.inactiveTab, flex: 1 }
              }
              onClick={() => this.setState({ teamManagementTab: 'teamSettings' })}
            >
              Team Settings
            </button>
            <button
              type='button'
              className='jr-btn-no-border pointer'
              style={
                this.state.teamManagementTab === 'teams'
                  ? { ...styles.activeTab, flex: 1 }
                  : { ...styles.inactiveTab, flex: 1 }
              }
              onClick={() => this.setState({ teamManagementTab: 'teams' })}
            >
              Teams
            </button>
            {this.state.enableLeagues && (
              <button
                type='button'
                className='jr-btn-no-border pointer'
                style={
                  this.state.teamManagementTab === 'leagues'
                    ? { ...styles.activeTab, flex: 1 }
                    : { ...styles.inactiveTab, flex: 1 }
                }
                onClick={() => this.setState({ teamManagementTab: 'leagues' })}
              >
                Leagues
              </button>
            )}
          </div>
        )}
        {isCatalogueMode ? <GroupCatalogue /> : (
          <form onSubmit={this.createLeaderboardConfig}>
            <div style={stickyActionBarStyle}>
              {isTeamsListTab && (
                <h1 style={styles.stickyTitle}>Teams</h1>
              )}
              <div style={styles.stickyActions}>
                {this.state.saveError && (
                  <div style={styles.saveError}>
                    {this.state.saveError}
                  </div>
                )}
                <div style={styles.buttonRow}>
                  {this.state.hasUpdated && (
                    <span style={styles.unsavedChangesHint}>
                      Remember to Save Changes
                    </span>
                  )}
                  <button
                    type='button'
                    className='jr-btn-default-outlined'
                    style={styles.stickyActionButton}
                    onClick={this.discardLeaderboardConfig}
                    disabled={
                      !this.state.hasUpdated
                      && this.props.formType === 'update'
                    }
                  >
                    Discard
                  </button>
                  <button
                    type='submit'
                    className='jr-btn-default'
                    style={styles.stickyActionButton}
                    disabled={
                      !this.state.hasUpdated
                      || !!this.state.groupLinkError
                      || this.state.isSaving
                      || (!isInterestGroupMode && this.isDailyTeamParticipationBonusInvalid())
                      || (!isInterestGroupMode && isBroken)
                    }
                  >
                    {saveTitle()}
                  </button>
                </div>
              </div>
            </div>
            {this.state.activeTab === 'settings' && isInterestGroupMode && (
              <InterestGroupSettingsTab
                formData={this.state.formData}
                isUpdate={this.props.formType === 'update'}
                handleInputChange={this.handleInputChange}
                handleCheckboxChange={this.handleCheckboxChange}
              />
            )}
            {this.state.activeTab === 'settings' && !isInterestGroupMode && (
              <LeaderboardSettingsTab
                formData={this.state.formData}
                orderedGyms={orderedGyms}
                timezones={timezones}
                isOrgAdmin={this.props.isOrgAdmin}
                allGyms={this.state.allGyms}
                isUpdate={this.props.formType === 'update'}
                prizesAndNotes={this.state.prizesAndNotes}
                newPrizeOrNote={this.state.newPrizeOrNote}
                groupLinkError={this.state.groupLinkError}
                selectedActivities={this.state.selectedActivities}
                activityTypeOptions={activityTypeOptions}
                handleInputChange={this.handleInputChange}
                handleSelectChange={this.handleSelectChange}
                handleCheckboxChange={this.handleCheckboxChange}
                handlePrizeOrNoteChange={this.handlePrizeOrNoteChange}
                handleAddPrizeOrNote={this.handleAddPrizeOrNote}
                handleDeletePrizeOrNote={this.handleDeletePrizeOrNote}
                onActivityTypesChange={(selected) => this.setState({
                  selectedActivities: selected.map((item) => item.value),
                  hasUpdated: true,
                })}
              />
            )}
            {this.state.activeTab === 'teams' && this.state.teamManagementTab === 'teamSettings' && (
              <TeamSettingsTab
                enableLeagues={this.state.enableLeagues}
                onEnableLeaguesChange={this.handleEnableLeaguesChange}
                showDailyTeamParticipationBonus={
                  isTeamParticipationBonusEligible(this.state.formData)
                }
                enableDailyTeamParticipationBonus={this.state.enableDailyTeamParticipationBonus}
                onEnableDailyTeamParticipationBonusChange={
                  this.handleEnableDailyTeamParticipationBonusChange
                }
                dailyTeamParticipationBonusMaxXp={
                  this.state.formData.dailyTeamParticipationBonusMaxXp
                }
                onDailyTeamParticipationBonusMaxXpChange={
                  this.handleDailyTeamParticipationBonusMaxXpChange
                }
              />
            )}
            {this.state.activeTab === 'teams' && this.state.teamManagementTab === 'leagues' && (
              <LeaguesTab
                leagueConfigs={this.state.leagueConfigs}
                onAddLeague={this.handleAddLeague}
                onDeleteLeague={this.handleDeleteLeague}
                onUpdateLeague={this.handleUpdateLeague}
              />
            )}
            {this.state.activeTab === 'teams' && this.state.teamManagementTab === 'teams' && (
              <TeamsTab
                teamConfigs={this.state.teamConfigs}
                leagueConfigs={this.state.leagueConfigs}
                competitionType={this.state.formData.competitionType}
                enableLeagues={this.state.enableLeagues}
                onAddTeam={this.handleAddTeam}
                onUpdateTeam={this.handleUpdateTeam}
              />
            )}
          </form>
        )}
      </div>
    );
  }

  private isDailyTeamParticipationBonusInvalid = () => (
    this.state.enableDailyTeamParticipationBonus
    && isTeamParticipationBonusEligible(this.state.formData)
    && !(
      typeof this.state.formData.dailyTeamParticipationBonusMaxXp === 'number'
      && Number.isInteger(this.state.formData.dailyTeamParticipationBonusMaxXp)
      && this.state.formData.dailyTeamParticipationBonusMaxXp >= 1
    )
  );

  private isBroken = (): boolean => {
    const hasActivityRestriction = this.state.selectedActivities.length > 0;
    return hasActivityRestriction
      && isExperiencePointsMetric({ metric: this.state.formData.metric });
  };

  /// Input Related Functions
  private handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: name === 'capMinutes'
          ? (checked ? 60 : undefined)
          : checked,
      },
      hasUpdated: true,
    }));
  };

  private handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
    | React.ChangeEvent<HTMLSelectElement>
    | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    let groupLinkError = this.state.groupLinkError;

    if (name === 'groupLink') {
      if (value.trim() === '') {
        groupLinkError = '';
      } else if (!this.isValidURL(value)) {
        groupLinkError = 'Please enter a valid URL.';
      } else {
        groupLinkError = '';
      }
    }

    if (type === 'date') {
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          [name]: value ? new Date(value) : undefined,
        },
        hasUpdated: true,
      }));
    } else if (type === 'number') {
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          [name]: value === '' ? undefined : Number(value),
        },
        hasUpdated: true,
      }));
    } else {
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          [name]: value,
        },
        groupLinkError,
        hasUpdated: true,
      }));
    }
  };

  private handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    this.setState((prevState) => {
      const nextFormData: ILeaderboardConfig = {
        ...prevState.formData,
        [name]: value,
      };
      if (name === 'competitionType') {
        nextFormData.metric = value === 'inter-team'
          ? 'team-weighted-minutes-score'
          : 'total-minutes';
      }
      const stillEligible = isTeamParticipationBonusEligible(nextFormData);
      return {
        formData: stillEligible
          ? nextFormData
          : {
            ...nextFormData,
            dailyTeamParticipationBonusMaxXp: undefined,
          },
        enableDailyTeamParticipationBonus: stillEligible
          ? prevState.enableDailyTeamParticipationBonus
          : false,
        hasUpdated: true,
        saveError: '',
      };
    });
  };

  private handleConfigTypeChange = (configType: ConfigType) => {
    this.setState((prevState) => {
      if (prevState.configType === configType) {
        return null;
      }
      const formData = { ...prevState.formData };
      let leaderboardFieldsBeforeInterestGroup = prevState.leaderboardFieldsBeforeInterestGroup;
      if (configType === 'interest-group') {
        leaderboardFieldsBeforeInterestGroup = captureLeaderboardFieldsOverriddenByInterestGroup(formData);
        applyInterestGroupInvariants(formData);
      } else {
        clearInterestGroupFields(formData, leaderboardFieldsBeforeInterestGroup);
        leaderboardFieldsBeforeInterestGroup = undefined;
      }
      return {
        configType,
        formData,
        leaderboardFieldsBeforeInterestGroup,
        activeTab: 'settings',
        hasUpdated: true,
        saveError: '',
      };
    });
  };

  private handleEnableLeaguesChange = (enabled: boolean) => {
    this.setState((prevState) => ({
      enableLeagues: enabled,
      teamManagementTab: !enabled && prevState.teamManagementTab === 'leagues'
        ? 'teamSettings'
        : prevState.teamManagementTab,
      leagueConfigs: enabled
        ? leagueConfigsWhenEnablingLeagues(prevState.leagueConfigs)
        : prevState.leagueConfigs,
      hasUpdated: true,
    }));
  };

  private handleEnableDailyTeamParticipationBonusChange = (enabled: boolean) => {
    this.setState((prevState) => ({
      enableDailyTeamParticipationBonus: enabled,
      formData: {
        ...prevState.formData,
        dailyTeamParticipationBonusMaxXp: enabled
          ? prevState.formData.dailyTeamParticipationBonusMaxXp
          : undefined,
      },
      hasUpdated: true,
    }));
  };

  private handleDailyTeamParticipationBonusMaxXpChange = (value: number | undefined) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        dailyTeamParticipationBonusMaxXp: value,
      },
      hasUpdated: true,
    }));
  };

  /// Team Related Functions
  private handleAddTeam = (team: ITeamConfig) => {
    const teamName = team.name;
    if (teamName) {
      this.setState((prevState) => ({
        teamConfigs: [...prevState.teamConfigs, team],
        hasUpdated: true,
      }));
    }
  };

  private handleDeleteTeam = (index: number) => {
    this.setState((prevState) => ({
      teamConfigs: prevState.teamConfigs.filter((_, i) => i !== index),
      hasUpdated: true,
    }));
  };

  private handleUpdateTeam = (index: number, team: ITeamConfig) => {
    this.setState((prevState) => ({
      teamConfigs: prevState.teamConfigs.map((t, i) => (i === index ? team : t)),
      hasUpdated: true,
    }));
  };

  /// League Related Functions
  private handleAddLeague = (league: ILeagueConfig) => {
    if (league.displayName) {
      this.setState((prevState) => ({
        leagueConfigs: [...prevState.leagueConfigs, league],
        hasUpdated: true,
      }));
    }
  };

  private handleDeleteLeague = (index: number) => {
    this.setState((prevState) => {
      const deletedLeagueId = prevState.leagueConfigs[index]?.id;
      const updatedTeamConfigs = deletedLeagueId
        ? prevState.teamConfigs.map((team) =>
          team.league === deletedLeagueId ? { ...team, league: undefined } : team)
        : prevState.teamConfigs;
      return {
        leagueConfigs: prevState.leagueConfigs.filter((_, i) => i !== index),
        teamConfigs: updatedTeamConfigs,
        hasUpdated: true,
      };
    });
  };

  private handleUpdateLeague = (index: number, league: ILeagueConfig) => {
    this.setState((prevState) => ({
      leagueConfigs: prevState.leagueConfigs.map((l, i) => (i === index ? league : l)),
      hasUpdated: true,
    }));
  };

  /// Prize/Note Related Functions
  private handleAddPrizeOrNote = () => {
    const prizeOrNote = this.state.newPrizeOrNote.trim();
    if (prizeOrNote) {
      this.setState((prevState) => ({
        prizesAndNotes: [...prevState.prizesAndNotes, prizeOrNote],
        newPrizeOrNote: '',
        hasUpdated: true,
      }));
    }
  };

  private handleDeletePrizeOrNote = (index: number) => {
    this.setState((prevState) => ({
      prizesAndNotes: prevState.prizesAndNotes.filter((_, i) => i !== index),
      hasUpdated: true,
    }));
  };

  private handlePrizeOrNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ newPrizeOrNote: e.target.value });
  };

  private isValidURL = (urlString: string) => {
    try {
      return !!new URL(urlString);
    } catch (_) {
      return false;
    }
  };
}

const activityTypeOptions = Object.values(ActivityType).map((activityType) => ({
  label: getActivityTypeText(activityType),
  value: activityType,
}));

const styles: { [key: string]: CSSProperties } = {
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '16px',
    position: 'relative',
  },
  headerTitle: {
    margin: 0,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#333',
  },
  typeRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '16px',
  },
  typeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  tabRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  subTabRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    color: 'lightgrey',
    marginBottom: '16px',
    borderBottom: '1px solid',
    borderTop: '1px solid',
  },
  activeTab: {
    fontWeight: 'bold',
    borderBottom: '1px solid',
  },
  inactiveTab: {
    color: '#000',
    textDecoration: 'none',
  },
  saveError: {
    color: 'red',
    textAlign: 'right',
  },
  stickyActionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0 12px',
    marginBottom: '8px',
    backgroundColor: '#fff',
  },
  stickyActionBarOnTeamsTab: {
    justifyContent: 'space-between',
  },
  stickyTitle: {
    margin: 0,
  },
  stickyActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
  },
  stickyActionButton: {
    boxSizing: 'border-box',
    minWidth: '96px',
    minHeight: '34px',
    lineHeight: '17px',
  },
  unsavedChangesHint: {
    fontSize: '1.25rem',
    fontWeight: 700,
    fontStyle: 'italic',
    color: '#555',
    whiteSpace: 'nowrap',
  },
};

const mapDispatchToProps = (dispatch: any) => ({
  createLeaderboardConfig: async (
    selectGymName: string,
    leaderboardConfig: ILeaderboardConfig,
  ) => {
    await dispatch(createLeaderboardConfig({
      gym: selectGymName,
      leaderboardConfig,
    })).unwrap();
  },
  updateLeaderboardConfig: async (
    selectGymName: string,
    leaderboardConfig: ILeaderboardConfig,
  ) => {
    await dispatch(updateLeaderboardConfig({
      gym: selectGymName,
      leaderboardConfig,
    })).unwrap();
  },
  refreshGymLeaderboardConfigs: (gymName: string) => dispatch(getLeaderboardConfigs(gymName)),
});

const mapStateToProps = (state: IAppStore) => {
  const { leaderboards, adminSettings } = state;
  const { selectedGymName, gyms } = adminSettings.data;
  const { isOrgAdmin } = adminSettings;
  const { isFetching } = state.leaderboards.view;
  const {
    selectedLeaderboardConfig,
  } = leaderboards.data;

  return {
    selectedGymName,
    selectedLeaderboardConfig,
    isOrgAdmin,
    gyms,
    isFetching,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(LeaderboardConfigForm));
